import { GeoJSONSource, type Map as MlMap } from 'maplibre-gl'
import type { BusRouteDetail } from '../../busOps/types'
import { CC } from '../../cc/tokens'

/**
 * One bus route's stops and speed-profile segments -- the SELECTED route only. An
 * always-visible "every route at once" overview layer was tried and removed: at city
 * scale, 70+ overlapping routes read as an undifferentiated mesh no matter how the line
 * styling was tuned, and clicking a specific line out of that mesh was never reliable --
 * confirmed directly ("remove the blue lines"). A route list is what actually makes
 * picking a route reliable; the map's job is to show ONE route well once it's picked, not
 * to be the picker itself.
 *
 * Segments are drawn as straight lines between their two stop endpoints, not the true
 * OSM-routed polyline -- bus_route_segment does not persist the routed path (only
 * tools/bus_speed_profile.py holds it, in-memory, at build time), so a straight line
 * between adjacent stops is what the data actually supports, same tradeoff
 * tools/bus_reconcile_speed.py's reconciliation query already makes for the same reason.
 *
 * FLAT LINE COLOUR, NOT A HEURISTIC. This line used to be coloured by speed-divergence
 * (observed p85 vs. the computed limit) or by batch congestion score -- both real
 * signals, but painting either as a green/amber/red route line sat one click away from
 * Google's own live TrafficLayer and read as a second, conflicting "traffic" indicator
 * that wasn't actually live traffic at all ("this is not real, showing red still, not
 * possible" -- direct instruction). Traffic-looking colour now belongs to Google's layer
 * alone; this line is a single flat colour so it never gets mistaken for one again.
 * limit_kph/observed_p50/p85_kph/sample_size are still carried in each segment's
 * properties for the click popup -- that data didn't stop being real, it just stopped
 * being painted as if it were congestion.
 */

export const SRC_BUS_STOPS = 'bus-route-stops'
export const SRC_BUS_SEGMENTS = 'bus-route-segments'
export const LYR_BUS_SEGMENT_CASING = 'bus-route-segment-casing'
export const LYR_BUS_SEGMENT_LINE = 'bus-route-segment-line'
export const LYR_BUS_STOPS = 'bus-route-stops-circle'
export const LYR_BUS_STOP_LABELS = 'bus-route-stop-labels'

const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

/** The route line's one and only colour -- see the flat-colour rationale above. */
export const ROUTE_LINE_COLOR = '#2563EB'

export function busRouteFeatures(route: BusRouteDetail | null): {
  stops: GeoJSON.FeatureCollection
  segments: GeoJSON.FeatureCollection
} {
  if (!route) return { stops: empty, segments: empty }
  const byStopSeq = new Map(route.stops.map((s) => [s.sequence_no, s]))

  const stopFeatures: GeoJSON.Feature[] = route.stops.map((s) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
    properties: { sequence_no: s.sequence_no, stop_name: s.stop_name },
  }))

  const segmentFeatures: GeoJSON.Feature[] = []
  for (const seg of route.segments) {
    const from = byStopSeq.get(seg.from_seq)
    const to = byStopSeq.get(seg.to_seq)
    if (!from || !to) continue
    // The real routed path when it exists -- a straight line between stops is what
    // "flying off the road" looks like on anything but a dead-straight street. Falls
    // back to the straight line only for segments built before polyline existed, or
    // where routing failed to connect the two stops (route_polyline returned None for
    // that gap) -- absence of data is not evidence of a problem, same rule this file's
    // tone-on-no-observed-data already follows.
    const coordinates = seg.polyline && seg.polyline.length >= 2
      ? seg.polyline.map(([lat, lon]) => [lon, lat])
      : [[from.lon, from.lat], [to.lon, to.lat]]
    segmentFeatures.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates },
      properties: {
        from_seq: seg.from_seq,
        to_seq: seg.to_seq,
        limit_kph: seg.limit_kph,
        source: seg.source,
        observed_p50_kph: seg.observed_p50_kph,
        observed_p85_kph: seg.observed_p85_kph,
        sample_size: seg.sample_size,
      },
    })
  }

  return {
    stops: { type: 'FeatureCollection', features: stopFeatures },
    segments: { type: 'FeatureCollection', features: segmentFeatures },
  }
}

/** The route's full road-following path as one stitched [lat, lon] list, for map engines
 *  (Google's Polyline) that want a single path rather than busRouteFeatures' per-segment
 *  LineStrings (which stay per-segment so MapLibre's segment click popup keeps its own
 *  limit/observed-speed properties). Same seg.polyline-else-straight-line fallback. */
export function routeLatLngPath(route: BusRouteDetail | null): [number, number][] {
  if (!route) return []
  const byStopSeq = new Map(route.stops.map((s) => [s.sequence_no, s]))
  const path: [number, number][] = []
  for (const seg of route.segments) {
    const from = byStopSeq.get(seg.from_seq)
    const to = byStopSeq.get(seg.to_seq)
    if (!from || !to) continue
    const pts: [number, number][] = seg.polyline && seg.polyline.length >= 2
      ? seg.polyline
      : [[from.lat, from.lon], [to.lat, to.lon]]
    for (const pt of pts) {
      const last = path[path.length - 1]
      if (!last || last[0] !== pt[0] || last[1] !== pt[1]) path.push(pt)
    }
  }
  return path
}

export function addBusRouteLayers(map: MlMap): void {
  if (!map.getSource(SRC_BUS_SEGMENTS)) {
    map.addSource(SRC_BUS_SEGMENTS, { type: 'geojson', data: empty })
  }
  if (!map.getSource(SRC_BUS_STOPS)) {
    map.addSource(SRC_BUS_STOPS, { type: 'geojson', data: empty })
  }

  if (!map.getLayer(LYR_BUS_SEGMENT_CASING)) {
    map.addLayer({
      id: LYR_BUS_SEGMENT_CASING,
      type: 'line',
      source: SRC_BUS_SEGMENTS,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#07080F',
        'line-opacity': 0.55,
        'line-width': ['interpolate', ['linear'], ['zoom'], 10, 5, 16, 10],
      },
    })
  }
  if (!map.getLayer(LYR_BUS_SEGMENT_LINE)) {
    map.addLayer({
      id: LYR_BUS_SEGMENT_LINE,
      type: 'line',
      source: SRC_BUS_SEGMENTS,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ROUTE_LINE_COLOR,
        'line-width': ['interpolate', ['linear'], ['zoom'], 10, 3, 16, 7],
      },
    })
  }
  if (!map.getLayer(LYR_BUS_STOPS)) {
    map.addLayer({
      id: LYR_BUS_STOPS,
      type: 'circle',
      source: SRC_BUS_STOPS,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3, 16, 6],
        'circle-color': '#FFFFFF',
        'circle-stroke-color': CC.info,
        'circle-stroke-width': 2,
      },
    })
  }
  if (!map.getLayer(LYR_BUS_STOP_LABELS)) {
    map.addLayer({
      id: LYR_BUS_STOP_LABELS,
      type: 'symbol',
      source: SRC_BUS_STOPS,
      minzoom: 13,
      layout: {
        'text-field': ['get', 'stop_name'],
        'text-size': 11,
        'text-offset': [0, 1.1],
        'text-anchor': 'top',
        'text-font': ['Instrument Sans Regular'],
      },
      paint: {
        'text-color': CC.muted,
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 1.4,
      },
    })
  }
}

export function setBusRouteData(map: MlMap, route: BusRouteDetail | null): void {
  const { stops, segments } = busRouteFeatures(route)
  const stopsSrc = map.getSource(SRC_BUS_STOPS)
  if (stopsSrc instanceof GeoJSONSource) stopsSrc.setData(stops)
  const segSrc = map.getSource(SRC_BUS_SEGMENTS)
  if (segSrc instanceof GeoJSONSource) segSrc.setData(segments)
}
